/**
 * Shared config + validation for metric normalization.
 *
 * Single source of truth used by:
 *   - The Metric Normalization picker widget (Add/Edit custom metric)
 *   - Any future server-side Zod tightening on metric_definitions writes
 *
 * The 4 core techniques and their valid direction pairings match what the
 * Normalize engine (`src/lib/data-hub/phase2/normalize/`) actually honors.
 * Legacy `inverse_percentile` and `inverse_mapping` are auto-mapped to
 * their modern equivalents when reading existing rows.
 */
export type NormType =
  | "percentile_ranking"
  | "linear_scaling"
  | "direct_mapping"
  | "custom_scaling";

export type NormDirection = "higher_better" | "lower_better" | "neutral";

export type CustomMethod = "escalations_trend_30d";

export type NormalizationValue = {
  normalization_type: NormType;
  normalization_direction: NormDirection;
  scale_min?: number | null;
  scale_max?: number | null;
  custom_method?: CustomMethod | null;
};

export type TechniqueSpec = {
  id: NormType;
  label: string;
  description: string;
  allowedDirections: NormDirection[];
  /** When true, direction is fully determined by the technique/method. */
  directionLocked?: boolean;
  requires: {
    scaleRange?: boolean;
    customMethod?: boolean;
  };
};

export const TECHNIQUES: TechniqueSpec[] = [
  {
    id: "percentile_ranking",
    label: "Percentile ranking",
    description:
      "Ranks the raw value against p10–p90 anchors from organization benchmarks (stepwise interpolation). Best when you have real distributions across accounts.",
    allowedDirections: ["higher_better", "lower_better", "neutral"],
    requires: {},
  },
  {
    id: "linear_scaling",
    label: "Linear scaling",
    description:
      "Maps (min, max) → 0–100 linearly. Use when the metric has a fixed, known range (e.g. NPS −100..100, satisfaction 1..10).",
    allowedDirections: ["higher_better", "lower_better"],
    requires: { scaleRange: true },
  },
  {
    id: "direct_mapping",
    label: "Direct mapping",
    description:
      "Numeric pass-through. Values in 0..1 are multiplied by 100; otherwise clamped to 0..100. Use when the raw value is already on a 0–100 (or 0–1) scale.",
    allowedDirections: ["higher_better", "lower_better"],
    requires: {},
  },
  {
    id: "custom_scaling",
    label: "Custom scaling",
    description:
      "Method-routed formula. Direction is fixed by the chosen method.",
    allowedDirections: ["higher_better", "lower_better", "neutral"],
    directionLocked: true,
    requires: { customMethod: true },
  },
];

export const CUSTOM_METHODS: {
  id: CustomMethod;
  label: string;
  direction: NormDirection;
  description: string;
}[] = [
  {
    id: "escalations_trend_30d",
    label: "Escalations trend (30d)",
    direction: "lower_better",
    description:
      "Maps trend value or categorical (declining/stable/worsening) to a 0–100 score where fewer escalations = higher score.",
  },
];

export const DIRECTION_LABELS: Record<NormDirection, string> = {
  higher_better: "Higher is better",
  lower_better: "Lower is better",
  neutral: "Neutral",
};

/** Map legacy technique names to their modern equivalent so old rows load
 *  cleanly into the picker. */
export function normalizeLegacyType(type: string | null | undefined): {
  type: NormType | null;
  direction: NormDirection | null;
} {
  if (!type) return { type: null, direction: null };
  const t = type.trim();
  if (t === "inverse_percentile")
    return { type: "percentile_ranking", direction: "lower_better" };
  if (t === "inverse_mapping")
    return { type: "direct_mapping", direction: "lower_better" };
  if (
    t === "percentile_ranking" ||
    t === "linear_scaling" ||
    t === "direct_mapping" ||
    t === "custom_scaling"
  ) {
    return { type: t as NormType, direction: null };
  }
  return { type: null, direction: null };
}

export function techniqueSpec(type: NormType): TechniqueSpec {
  const spec = TECHNIQUES.find(t => t.id === type);
  if (!spec) throw new Error(`Unknown normalization type: ${type}`);
  return spec;
}

export function validateNormalization(v: Partial<NormalizationValue>): {
  ok: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  if (!v.normalization_type) {
    errors.push("Select a normalization technique.");
    return { ok: false, errors };
  }
  const spec = TECHNIQUES.find(t => t.id === v.normalization_type);
  if (!spec) {
    errors.push("Unknown technique.");
    return { ok: false, errors };
  }
  if (!v.normalization_direction) {
    errors.push("Select a direction.");
  } else if (!spec.allowedDirections.includes(v.normalization_direction)) {
    errors.push(
      `Direction "${v.normalization_direction}" is not valid for ${spec.label}.`
    );
  }
  if (spec.requires.scaleRange) {
    const lo = v.scale_min;
    const hi = v.scale_max;
    if (lo === null || lo === undefined || Number.isNaN(lo)) {
      errors.push("Scale minimum is required for linear scaling.");
    }
    if (hi === null || hi === undefined || Number.isNaN(hi)) {
      errors.push("Scale maximum is required for linear scaling.");
    }
    if (
      typeof lo === "number" &&
      typeof hi === "number" &&
      Number.isFinite(lo) &&
      Number.isFinite(hi) &&
      hi <= lo
    ) {
      errors.push("Scale maximum must be greater than scale minimum.");
    }
  }
  if (spec.requires.customMethod) {
    if (!v.custom_method) {
      errors.push("Choose a custom scaling method.");
    } else {
      const method = CUSTOM_METHODS.find(m => m.id === v.custom_method);
      if (!method) errors.push("Unknown custom scaling method.");
      else if (
        v.normalization_direction &&
        v.normalization_direction !== method.direction
      ) {
        errors.push(
          `Method "${method.label}" forces direction "${DIRECTION_LABELS[method.direction]}".`
        );
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

/** Short human summary used on the trigger button and read-only badges. */
export function summarizeNormalization(v: Partial<NormalizationValue>): string {
  if (!v.normalization_type) return "Not set";
  const spec = TECHNIQUES.find(t => t.id === v.normalization_type);
  const techLabel = spec?.label ?? v.normalization_type;
  const dirLabel = v.normalization_direction
    ? DIRECTION_LABELS[v.normalization_direction]
    : "—";
  if (v.normalization_type === "custom_scaling" && v.custom_method) {
    const method = CUSTOM_METHODS.find(m => m.id === v.custom_method);
    return `${techLabel} · ${method?.label ?? v.custom_method}`;
  }
  if (
    v.normalization_type === "linear_scaling" &&
    (v.scale_min != null || v.scale_max != null)
  ) {
    return `${techLabel} · [${v.scale_min ?? "?"}, ${v.scale_max ?? "?"}] · ${dirLabel}`;
  }
  return `${techLabel} · ${dirLabel}`;
}
